import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Cart, DeliveryMode, DeliveryModeType } from '@boels/models';
import {
  CartFacade,
  DeliveryFacade,
  ProductStockFacade,
} from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  catchError,
  EMPTY,
  filter,
  first,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { deliveryCheckoutSteps } from '../checkout-step/checkout-step.config';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { EcommerceTrackingService } from '@boels/helpers';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'boels-checkout-delivery-mode',
  templateUrl: './checkout-delivery-mode.component.html',
  styleUrls: ['./checkout-delivery-mode.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['deliveryMode'],
    },
    ProductStockFacade,
  ],
  imports: [
    TranslocoModule,
    SpinnerComponent,
    FontAwesomeModule,
    ButtonComponent,
  ],
})
export class CheckoutDeliveryModeComponent {
  #productStockFacade = inject(ProductStockFacade);
  #deliveryFacade = inject(DeliveryFacade);
  #router = inject(Router);
  #cartFacade = inject(CartFacade);
  #ecommerceTrackingService = inject(EcommerceTrackingService);

  supportedDeliveryMethods$ = toSignal(
    this.#cartFacade.updateCartMode().pipe(
      switchMap(() => this.#deliveryFacade.getDeliveryModes()),
      // reverse the order of the delivery modes
      tap((modes) => modes.reverse()),
    ),
  );

  back(): void {
    this.#router.navigate(['/checkout/customer-details']);
  }

  setDeliveryMode(mode: DeliveryMode): void {
    this.#deliveryFacade
      .setDeliveryMode(mode)
      .pipe(
        first(),
        switchMap(() => this.#cartFacade.getActiveCart()),
        filter(Boolean),
        tap((cart: Cart) => {
          let nextStepUrl = deliveryCheckoutSteps[0].url;
          if (mode.code === DeliveryModeType.PICKUP) {
            this.#preFetchPickupPointOfService(cart);
            nextStepUrl = deliveryCheckoutSteps[1].url;
          }
          this.#ecommerceTrackingService.setShippingInfoEvent(mode.code);
          this.#cartFacade.setDeliveryMode(mode);
          this.#router.navigate([nextStepUrl]);
        }),
        catchError(() => {
          throwError(() => 'Could not set delivery mode');
          this.back();
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  #preFetchPickupPointOfService(cart: Cart): void {
    const query = cart.paymentInfo?.billingAddress?.formattedAddress as string;
    const productCode = cart?.entries?.map(
      (entry) => entry.product?.code,
    ) as string[];
    const searchConfig = {
      pageSize: 10,
      currentPage: 0,
    };
    this.#productStockFacade.setSelectedProductStock();
    this.#productStockFacade.loadClosestStores(
      productCode,
      query,
      searchConfig,
    );
  }

  protected readonly chevronLeft = faChevronLeft;
  protected readonly DeliveryModeType = DeliveryModeType;
  protected readonly faChevronLeft = faChevronLeft;
}
